// @ts-check
import React from 'react';
import dayjs from 'dayjs';

import Tab from '@liquidweb-dev/undercurrent/Tab';
import Tabs from '@liquidweb-dev/undercurrent/Tabs';
import FullScreenError from 'components/legacy/FullScreenError';
import Attributes from 'components/HostingDetailsAttributes';
import Description from 'components/HostingDetailsDescription';
import PageLayout, { PageLayoutInsetContent } from 'components/PageLayout';
import TabBar from 'components/TabBar';
import CancellationAlert from 'components/CancellationAlert';
import hostingDetailsAlsowith from 'pages/servers/details/hostingDetailsAlsowith';
import useAssetDetails from 'modules/queries/asset/useDetails';
import NotFound from 'pages/NotFound';
import {
	Redirect,
	Link as RouterLink,
	useLocation,
	useParams,
} from 'react-router-dom';
import { getErrorStringHelper } from 'utility/redux/selectorHelperFunctions/apiModules';
import useDocumentTitle from 'utility/hooks/useDocumentTitle';
import useTriggerServerViewEvent from 'utility/hooks/useTriggerServerViewEvent';
import Actions from './Actions';

/**
 * A layout for VMware.vCloud.vDC servers' details page.
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
interface VmWareVdcLayoutProps {
	children: React.ReactNode;
}

const VmWareVdcLayout = ({ children }: VmWareVdcLayoutProps) => {
	/** @type {{uniqId: string}} */
	const { uniqId } = useParams<{ uniqId: string }>();
	const { pathname } = useLocation();

	const {
		data: assetDetailsData,
		isLoading: assetDetailsIsLoading,
		isError: assetDetailsIsError,
		error: assetDetailsError,
	} = useAssetDetails({
		uniq_id: uniqId,
		alsowith: hostingDetailsAlsowith,
	});

	const vmwareDocumentTitle =
		assetDetailsData?.custom_name || assetDetailsData?.domain || '';
	useDocumentTitle(vmwareDocumentTitle || 'Server Details');
	useTriggerServerViewEvent({ uniqId });

	if (assetDetailsIsError && assetDetailsError.statusCode === 404) {
		return <NotFound />;
	}

	if (assetDetailsIsError) {
		return (
			<FullScreenError content={getErrorStringHelper(assetDetailsIsError)} />
		);
	}

	const {
		custom_name: customName,
		custom_description: customDescription,
		domain,
		isVmware,
	} = assetDetailsData || {};

	// redirect non VMware.vCloud.vDC servers
	if (isVmware === false) {
		return <Redirect to={`/servers/${uniqId}`} />;
	}

	// the tabs correspond to the last portion of the path after 'vmware-vdc'
	const tabsValue = pathname.split('/')[4] || 'overview';

	const title = customName || domain || '';
	// prevent extra gap in PageLayout
	const shouldRenderDescription =
		Boolean(customDescription) || !(title === domain);

	const cancellationDate =
		assetDetailsData?.scheduledCancellationDate?.DateTime;
	const isCancellationScheduled = cancellationDate
		? dayjs(cancellationDate).isSameOrAfter(dayjs(), 'day')
		: false;

	const nextRenewalDate = dayjs(
		assetDetailsData?.nextInvoiceDate?.DateTime,
	).format('MMMM D, YYYY');

	return (
		<>
			{isCancellationScheduled && (
				<CancellationAlert
					cancellationDate={cancellationDate}
					uniqId={uniqId}
					title={title}
					nextRenewalDate={nextRenewalDate}
				/>
			)}
			<PageLayout
				title={title}
				attributesSlot={<Attributes />}
				actionsSlot={<Actions />}
				isLoading={assetDetailsIsLoading}
				description={
					shouldRenderDescription && (
						<Description serverDescription={customDescription} />
					)
				}
				variant="dynamic"
			>
				<TabBar sx={{ marginBottom: 600 }}>
					<Tabs value={tabsValue} variant="scrollable">
						<Tab
							component={RouterLink}
							value="overview"
							to={`/servers/${uniqId}/vmware-vdc`}
							label="Overview"
						/>
						<Tab
							component={RouterLink}
							value="settings"
							to={`/servers/${uniqId}/vmware-vdc/settings`}
							label="Settings"
						/>
					</Tabs>
				</TabBar>
				<PageLayoutInsetContent sx={{ flexGrow: 1 }}>
					{children}
				</PageLayoutInsetContent>
			</PageLayout>
		</>
	);
};

export default VmWareVdcLayout;
